import { Component, Inject, OnDestroy, OnInit, inject } from '@angular/core';
import { StateService } from '@uirouter/core';
import { SpacesService } from 'account-hybrid/common/components/spaces';
import { SessionService } from 'account-hybrid/core/authentication';
import { Environment, ENVIRONMENT_TOKEN } from 'account-hybrid/core/environment.service';
import { buildAzureStoredPhotoUrl } from 'apps/account/src/account-shared/pipes/azure-stored-photo.util';
import { ParseService } from 'apps/account/src/account-shared/services/parse.service';
import { dateSerialize } from 'apps/account/src/account-shared/pipes/date-serializer.util';
import { SnapshotService } from 'apps/account/src/account-shared/services/snapshot.service';
import { NativeCurationModalService } from '../../../common/components/curation-modal/native/native-curation-modal.service';
import * as AudiencesActions from '../../../../account-v2/src/app/features/advocacy/group/audiences/actions';
import AudiencesApi from '../../../../account-v2/src/app/actions/api/audiencesApi';
import PropertyApi from '../../../../account-v2/src/app/actions/propertyApi';
import JourneysActions from '../../../../account-v2/src/app/features/advocacy/group/journeys/actions';
import { Contests } from '../../../../account-v2/src/app/features/advocacy/group/audiences/constants';
import { AdvocacyNgReduxFacade } from '../store/advocacy-ngredux.facade';
import { NativeCampaignContainerService } from '../touchpoint-sheets/native-campaign-container.service';
import { CreateContestModalService } from '../create-contest-modal/create-contest-modal.service';

/**
 * Native-Angular port of the AngularJS `ftAudiences` controller
 * (account-v2/.../advocacy/group/audiences/audiences.component.ts).
 *
 * Drives the /advocacy/audiences leaf. Follows the bigPicture/contests
 * recipe — advocacy state through the native NgRx facade, action thunks as
 * ES imports. Fully drained of `$injector` (inc 2y: the create-contest
 * modal is the native CreateContestModalService).
 *
 * State slice this binds to (same shape as v1's `$ngRedux.connect`):
 *   { ...state.views.guestsJourneys,        // isInitialized etc.
 *     ...state.apiData.propertyGuestsJourneys, // photoAdvocacy / prestayAdvocacy /
 *                                              // poststayAdvocacy / homepageAdvocacy
 *                                              // / quoteListAdvocacy.isActive flags
 *     ...state.views.audiences,             // visitors / weddings / team / etc.
 *     prestayCampaigns: state.apiData.prestay,
 *     poststayCampaigns: state.apiData.poststay,
 *     photoAdvocacyCampaigns: state.apiData.propertyPlatformData.photoAdvocacyCampaigns,
 *     singleProperty: state.ui.context.singleProperty }
 *
 * Each of those slices spreads onto `this` via Object.assign in syncFromState,
 * preserving the v1 controller's template surface (vm.photoAdvocacy.isActive
 * etc. read as photoAdvocacy.isActive etc. in the native template).
 *
 * Most of the template (every <primary> / <secondary> slot in the 5
 * <ft-expandable-content> sections) is gated on these isActive flags which
 * are all FALSE for the QA-login parity property — so the screenshot only
 * captures the <collapsed> slots. The native template still emits the
 * gated branches (with the same conditions) so a property with active
 * advocacy renders the same expanded content as v1. The projection-slot
 * tags (<collapsed>/<primary>/<secondary>/<custom>/<panel>) and every
 * AngularJS-era directive those branches reference (<ng-pluralize>,
 * <ft-targets-modal>, <ft-story-highlight>, <ft-paging>, the `translate`
 * attribute form, …) are now native shims in LegacyShimsModule, so
 * AdvocacyGroupModule compiles under the stricter CUSTOM_ELEMENTS_SCHEMA
 * (no longer NO_ERRORS_SCHEMA). The only tags still relying on that schema
 * are the deferred <ft-modal> shells (People M5b) and the lone bare
 * <ui-view> — port those to finish removing the schema entirely.
 */
@Component({
    selector: 'ft-audiences',
    templateUrl: './audiences.component.html',
    standalone: false
})
export class AudiencesComponent implements OnInit, OnDestroy {
  property: any;
  canEditData = false;

  // Slice-derived properties spread onto this in syncFromState — loose typing
  // mirrors v1 controller surface (template binds to vm.X without type checks).
  photoAdvocacy: any;
  prestayAdvocacy: any;
  poststayAdvocacy: any;
  homepageAdvocacy: any;
  quoteListAdvocacy: any;
  photoAdvocacyCampaigns: any;
  prestayCampaigns: any;
  poststayCampaigns: any;
  guestsAndFriends: any;
  visitors: any;
  weddings: any;
  team: any;
  randomPhotos: any;
  seasonalPhotos: any;
  currentCampaignPhotos: any;
  singleProperty: any;
  isInitialized = false;

  contestConstants = Contests;
  isGeneralVariation: any;
  isTeamVariation: any;
  isWeddingVariation: any;

  sections: any = {};
  sectionsRegistry = [
    { name: 'guests', isActive: true, isDisabled: false },
    { name: 'visitors', isActive: false, isDisabled: false },
    { name: 'wedding', isActive: false, isDisabled: false },
    { name: 'fans', isActive: false, isDisabled: false },
    { name: 'team', isActive: false, isDisabled: false },
  ];

  seasonalPhotosStartDate: any;
  seasonalPhotosEndDate: any;

  /**
   * The audiences template includes 17+ <ft-modal> shells at the bottom of
   * the page whose internal scope (from the AngularJS ftModal directive)
   * publishes `$ftModal`, `id`, and `*CampaignsSortMode` on the surrounding
   * scope. They're not visible at QA-login but their template expressions
   * still need to type-check under AOT. Index signature keeps the
   * controller surface loose-typed (matches v1's untyped controller) so
   * those expressions resolve without us declaring every modal-scope
   * variable explicitly — the eventual ft-modal native shim will drop
   * these in favor of a typed modal API.
   */
  [key: string]: any;

  private unsubscribe?: () => void;
  private $ngRedux: any;
  // WS5 #10 inc 2y: the native create-contest modal launcher via a FIELD
  // INITIALIZER (inject() in ngOnInit throws NG0203); the last v1 grab,
  // 'ftCreateContestModal', is gone.
  private readonly createContestModal = inject(CreateContestModalService);
  private readonly parse = inject(ParseService);
  private campaignContainer: any;
  private curationModal: any;
  // WS5 #10 inc 2r: native SnapshotService via a FIELD INITIALIZER (inject() in
  // ngOnInit throws NG0203); the v1 'snapshot' grab is gone.
  private snapshot = inject(SnapshotService);
  private dateSerializer: any;

  constructor(
    private readonly spacesService: SpacesService,
    private readonly sessionService: SessionService,
    private readonly stateService: StateService,
    @Inject(ENVIRONMENT_TOKEN) private readonly environment: Environment,
    private readonly nativeCurationModal: NativeCurationModalService,
    private readonly advStore: AdvocacyNgReduxFacade,
    // WS5 Phase 3 #4: native campaign-container launcher (createComponent mount),
    // replacing $injector.get('CampaignContainer').
    private readonly nativeCampaignContainer: NativeCampaignContainerService,
  ) {}

  ngOnInit(): void {
    this.property = this.sessionService.getProperty(this.spacesService.getPropertyUuid()!);
    const ADVOCACY_WRITE = 2; // Permissions.AdvocacyWrite — see contests.component.ts.
    this.canEditData = (this.property?.Permissions & ADVOCACY_WRITE) === ADVOCACY_WRITE;

    // WS2 strangle: advocacy state now in the native NgRx feature (facade).
    this.$ngRedux = this.advStore;
    this.campaignContainer = this.nativeCampaignContainer;
    this.curationModal = this.nativeCurationModal; // TERMINAL CURATION CUT: native opener
    this.dateSerializer = dateSerialize;

    this.unsubscribe = this.$ngRedux.subscribe(() => this.syncFromState());
    this.syncFromState();
    this.fetchGuestsData();
  }

  ngOnDestroy(): void {
    this.unsubscribe?.();
  }

  private syncFromState(): void {
    const state = this.$ngRedux.getState();
    const photoAdvocacyCampaigns = state?.apiData?.propertyPlatformData?.photoAdvocacyCampaigns;
    if (photoAdvocacyCampaigns?.campaigns) {
      this.isGeneralVariation = !!photoAdvocacyCampaigns.campaigns.item.find(
        (campaign: any) => campaign.type == 255,
      );
      this.isTeamVariation = !!photoAdvocacyCampaigns.campaigns.item.find(
        (campaign: any) => campaign.theme == this.contestConstants.team,
      );
      this.isWeddingVariation = !!photoAdvocacyCampaigns.campaigns.item.find(
        (campaign: any) => campaign.theme == this.contestConstants.weddings,
      );
    }
    this.prestayCampaigns = state?.apiData?.prestay;
    this.poststayCampaigns = state?.apiData?.poststay;
    this.photoAdvocacyCampaigns = photoAdvocacyCampaigns;
    this.singleProperty = state?.ui?.context?.singleProperty;
    Object.assign(this, state?.views?.guestsJourneys ?? {});
    Object.assign(this, state?.apiData?.propertyGuestsJourneys ?? {});
    Object.assign(this, state?.views?.audiences ?? {});
  }

  fetchGuestsData(): void {
    this.$ngRedux.dispatch(AudiencesActions.initialized(false));
    Promise.all([
      this.$ngRedux.dispatch(PropertyApi.getAdvocacyGuestJourney(this.property.UUID)),
      this.$ngRedux.dispatch(AudiencesApi.guestsAndFriends(this.property.UUID)),
    ]).then(() => {
      this.$ngRedux.dispatch(AudiencesActions.initialized(true));
      this.$ngRedux.dispatch(PropertyApi.getPhotoAdvocacyLayerData(this.property.UUID));
      this.$ngRedux.dispatch(
        JourneysActions.fetchJourneysData(
          this.property.UUID,
          this.dateSerializer(new Date(this.snapshot.get().start)),
          this.dateSerializer(new Date(this.snapshot.get().end)),
        ),
      );
    });
  }

  changeSection(section: 'guestsAndFriends' | 'visitors' | 'weddings' | 'team'): void {
    if (!(this as any)[section]) {
      this.$ngRedux.dispatch(AudiencesActions.initialized(false));
      this.$ngRedux.dispatch((AudiencesApi as any)[section](this.property.UUID)).then(() => {
        this.$ngRedux.dispatch(AudiencesActions.initialized(true));
      });
    }
  }

  getFansAndFollowersData(): void {
    this.$ngRedux.dispatch(AudiencesActions.initialized(false));
    const date = new Date();
    this.seasonalPhotosStartDate = this.dateSerializer(new Date(date.getFullYear() - 1, date.getMonth() - 1, date.getDate()));
    this.seasonalPhotosEndDate = this.dateSerializer(new Date(date.getFullYear() - 1, date.getMonth() + 1, date.getDate()));
    Promise.all([
      this.$ngRedux.dispatch(AudiencesApi.randomPhotos(this.property.UUID, 1)),
      this.$ngRedux.dispatch(AudiencesApi.seasonalPhotos(this.property.UUID, 1, this.seasonalPhotosStartDate, this.seasonalPhotosEndDate)),
      this.$ngRedux.dispatch(AudiencesApi.currentCampaignPhotos(this.property.UUID, 1)),
    ]).then(() => {
      this.$ngRedux.dispatch(AudiencesActions.initialized(true));
    });
  }

  getContestBackgroundImage(contest: any): string {
    if (contest?.activityPhoto) {
      return buildAzureStoredPhotoUrl(contest.activityPhoto, undefined, this.environment.AzureCdnBaseUrl) as string;
    }
    return contest?.propertyBackgroundImageUrl ?? '';
  }

  /** Resolves `latestEntry | ftAzureStoredPhoto:'thumbnail'` (template filter call). */
  azurePhoto(photo: any, size?: string): string {
    return buildAzureStoredPhotoUrl(photo, size, this.environment.AzureCdnBaseUrl) as string;
  }

  showContest(contest: any, activeSection: string): void {
    this.stateService.go(`advocacy.audiences.${activeSection}`, { contestUuid: contest.id });
  }

  createNewContest(theme: number): void {
    // v1 (account-v2 audiences.component.ts:172) wrapped with
    // Parse.arrayOrDefault — clonableContests is the {item:…} JSON convention,
    // NOT a plain array (see contests.component.ts; the same pre-existing
    // `?? []` break, caught by the 2y route-abort diag). NO `?.` on
    // photoAdvocacyCampaigns: v1 had none either, so a fast click before
    // getPhotoAdvocacyLayerData resolves THROWS and no modal opens — verbatim.
    this.createContestModal.show(
      this.property.UUID,
      this.parse.arrayOrDefault(this.photoAdvocacyCampaigns.clonableContests),
      (contestUuid: string) => {
        // v1 also passed `context: this.fullContext` — provably dead (never
        // assigned in the v1 controller → always undefined, and the target
        // state declares no such param), so it is dropped here.
        this.stateService.go('advocacy.audiences.details', { contestUuid });
        this.$ngRedux.dispatch(PropertyApi.getPhotoAdvocacyLayerData(this.property.UUID));
      },
      () => alert('Could not create contest.'),
      theme,
    );
  }

  onCalendarDateRangeChange(dateRange: any): void {
    this.$ngRedux.dispatch(
      JourneysActions.dateRangeChange(
        this.property.UUID,
        this.dateSerializer(dateRange.fromDate),
        this.dateSerializer(dateRange.toDate),
      ),
    );
  }

  showCampaignDetails(layerName: string, campaignUuid: string): void {
    this.campaignContainer?.showExisting(this.property.UUID, campaignUuid, layerName);
  }

  showThemeCampaignDetails(theme: number): void {
    const campaign = this.photoAdvocacyCampaigns?.campaigns?.item?.find((c: any) => c.theme == theme);
    if (campaign) this.showCampaignDetails('photo-advocacy', campaign.id);
  }

  showGeneralThemeCampaignDetails(): void {
    const campaign = this.photoAdvocacyCampaigns?.campaigns?.item?.find((c: any) => c.type == 255);
    if (campaign) this.showCampaignDetails('photo-advocacy', campaign.id);
  }

  newCampaign(layerName: string): void {
    this.campaignContainer?.createNew(this.property.UUID, layerName);
  }

  openShareModal(story: any): void {
    this.curationModal?.new(this.property, story.CampaignPromoterUUID, true);
  }

  goToLibrary(): void {
    this.stateService.go('stories.photos');
  }

  currentCampaignPhotosApiPageChange(page: any): void {
    this.$ngRedux.dispatch(AudiencesApi.currentCampaignPhotos(this.property.UUID, page.page));
  }

  seasonalPhotosApiPageChange(page: any): void {
    this.$ngRedux.dispatch(
      AudiencesApi.seasonalPhotos(this.property.UUID, page.page, this.seasonalPhotosStartDate, this.seasonalPhotosEndDate),
    );
  }

  randomPhotosApiPageChange(page: any): void {
    this.$ngRedux.dispatch(AudiencesApi.randomPhotos(this.property.UUID, page.page));
  }

  /** Receives the ft-sections controller after its init, matching v1's
   *  `onSectionsInitialized(controller)` template handler. */
  onSectionsInitialized(controller: any): void {
    (this as any).sectionsController = controller;
  }

  resetSection(sectionName: string): void {
    if (this.sections[sectionName]) {
      this.sections[sectionName].forEach((val: any, index: number) => (val.isActive = index === 0));
    }
  }

  saveSectionsRegistry(sectionName: string, registry: any): void {
    if (this.sections[sectionName]) {
      this.sections[sectionName] = registry;
    }
  }
}
